import { useAtomValue } from 'jotai';
import { InfoIcon, LockIcon, ZapIcon } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { cn, TableCell, TableRow } from '@crossnokaye/ux-common';

import { Sequence, SequenceItem } from '@repo/sdk/models/components';

import { AddStageColumnCell } from '@/components/sequencer-table/add-stage-column';
import { EquipmentCell } from '@/components/sequencer-table/equipment-cell';
import {
  addStageToSequence,
  cycleStageInSequence,
  enableRowInSequence,
  getNestedValue,
  removeRowFromSequence,
  removeStageFromSequence,
} from '@/components/sequencer-table/helpers';
import { SequenceCell } from '@/components/sequencer-table/sequence-cell';
import { SequenceEditorTable } from '@/components/sequencer-table/sequence-editor-table';
import {
  cosmicActiveAtom,
  cosmicFallbackStageValueAtom,
  currentStageValueAtom,
  sequencesWithValuesAtom,
  SequenceWithValues,
  siblingSequenceValuesAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';

import { CompressorEnergyAI } from './compressor-energy-ai/compressor-energy-ai';
import { getCompressorHighlightState } from './compressor-highlight-state';
import { CompressorSequencerInstructionsContent } from './compressor-sequencer-instructions-content';
import {
  CompressorSequencerStage,
  cycleCompressorSequencerStageValue,
  deriveCompressorSequencerIcon,
  findLockingSibling,
  getCompressorSequencerCellStyles,
  getCompressorSequencerOverlayProperties,
  getCompressorSequenceState,
  trimBackground,
} from './helpers';
import { SequencerInstructions } from '../components/sequencer-instructions';
import { SequencerLegend } from '../components/sequencer-legend';
import { activeStageLegendItem, standardLegendItems } from '../components/sequencer-legend-items';
import { SequencerSectionTemplate } from '../components/sequencer-section-template';

/**
 * Returns the content of the cell for a compressor sequencer stage.
 *
 * @param stageValue - The stage value to get the cell content for.
 * @returns The content of the cell for a compressor sequencer stage.
 */
const getCompressorSequencerCellContent = (stageValue: number): React.ReactNode => {
  switch (stageValue) {
    case CompressorSequencerStage.UNAVAILABLE:
      return <LockIcon className={cn('glyph glyph-sm stroke-muted-foreground')} />;
  }
  return null;
};

const EnergyAIControlIndicator = () => (
  <span
    aria-label="Energy AI schedule control active"
    className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-conserving"
    data-component="EnergyAIControlIndicator"
  >
    <ZapIcon aria-hidden="true" className="h-2.5 w-2.5 fill-white stroke-white" />
  </span>
);

const FALLBACK_STAGE_TOOLTIP =
  'Stage that Energy AI will fallback to if cloud connectivity is lost';

const FallbackStageStatusPill = () => (
  <Tooltip>
    {/*
        A button rather than a span: Radix does not make an `asChild` target
        focusable, so the tooltip was pointer-only. The visible "FALL BACK" text
        is left to name the button - Radix supplies the explanation as its
        description once the tooltip opens on hover or focus.
      */}
    <TooltipTrigger asChild>
      <button
        className="inline-flex items-center gap-1 rounded-sm bg-ckds-base-white px-1.5 py-0.5 text-xs font-bold leading-none text-ckds-neutral-950 outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
        data-component="FallbackStageStatusPill"
        type="button"
      >
        FALL BACK
        <InfoIcon aria-hidden="true" className="h-3 w-3" />
      </button>
    </TooltipTrigger>
    <TooltipPortal>
      <TooltipContent align="center" side="bottom">
        <span>{FALLBACK_STAGE_TOOLTIP}</span>
        <TooltipArrow />
      </TooltipContent>
    </TooltipPortal>
  </Tooltip>
);

interface CompressorSequencerEditorProps {
  sequenceWithValues: SequenceWithValues;
}

const getStageValue = (
  sequence: Sequence | undefined,
  rowIndex: number,
  columnIndex: number
): number => {
  return sequence?.table?.[rowIndex]?.stageValues?.[columnIndex] || 0;
};

const CompressorSequenceEditor = ({ sequenceWithValues }: CompressorSequencerEditorProps) => {
  const form = useFormContext();
  const activeStage = useAtomValue(currentStageValueAtom);
  const cosmicActive = useAtomValue(cosmicActiveAtom);
  const cosmicFallbackStage = useAtomValue(cosmicFallbackStageValueAtom);
  const siblingSequences = useAtomValue(siblingSequenceValuesAtom);
  const { isDisabled: disabledByPermissions, tooltipText: disabledTooltipText } =
    useActionPermission({
      privilege: PRIVILEGES.writeCommands,
    });
  // Get the form field key from the ref ID
  const formFieldKey = sequenceWithValues.ref?.id ? `sequencer.${sequenceWithValues.ref.id}` : '';

  // Get the current sequence value from the form
  const sequenceValue = (formFieldKey ? form.watch(formFieldKey) : undefined) as
    | Sequence
    | undefined;

  const handleRemoveRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeRowFromSequence(sequence, rowIndex);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = enableRowInSequence(sequence, rowIndex, CompressorSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = addStageToSequence(sequence, CompressorSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleRemoveStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeStageFromSequence(sequence);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleStageClicked = (
    sequence: Sequence | undefined,
    rowIndex: number,
    columnIndex: number
  ) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = cycleStageInSequence(
      sequence,
      rowIndex,
      columnIndex,
      cycleCompressorSequencerStageValue
    );
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const maxStages = Math.max(
    ...(sequenceValue?.table?.map((seq: any) => seq.stageValues.length - 1) || [])
  );
  const columnTitles = Array.from({ length: maxStages }, (_, i) => `Stage ${i + 1}`);
  const rowTitles = useMemo((): string[] => {
    return sequenceValue?.table?.map((item: SequenceItem) => item.name) ?? [];
  }, [sequenceValue?.table]);

  const overlayProperties = getCompressorSequencerOverlayProperties(sequenceValue);
  const defaultValues = form?.formState?.defaultValues;
  const { highlightedStage, energyAIFallbackStage } = getCompressorHighlightState({
    activeStage,
    cosmicActive,
    cosmicFallbackStage,
  });
  const compressorLegendItems = [
    ...standardLegendItems,
    {
      icon: <div className="w-4 h-4 rounded-full" style={{ background: trimBackground }} />,
      label: 'Trim',
    },
    activeStageLegendItem,
  ];

  const originalSequence = defaultValues
    ? (getNestedValue(defaultValues, formFieldKey) as Sequence)
    : undefined;
  // Resolved once so the row cells and the Energy AI panel cannot disagree about
  // which compressors are locked: the rows need the sibling's label, the panel
  // needs the names, and both now read off this one map.
  const lockedByLabelByCompressor = useMemo(() => {
    const locked = new Map<string, string>();
    rowTitles.forEach((row) => {
      const lockedByLabel = findLockingSibling(row, siblingSequences);
      if (lockedByLabel) {
        locked.set(row, lockedByLabel);
      }
    });
    return locked;
  }, [rowTitles, siblingSequences]);
  const lockedCompressorNames = useMemo(
    () => [...lockedByLabelByCompressor.keys()],
    [lockedByLabelByCompressor]
  );
  const unavailableCompressorNames = useMemo((): string[] => {
    return (
      sequenceValue?.table
        ?.filter((row: SequenceItem) =>
          row.stageValues?.every(
            (stageValue: number) => stageValue === CompressorSequencerStage.UNAVAILABLE
          )
        )
        .map((row: SequenceItem) => row.name) ?? []
    );
  }, [sequenceValue?.table]);

  return (
    <Fragment key="compressor-sequencer-editor">
      <SequencerSectionTemplate
        legend={null}
        onAddStage={() => handleAddStageClicked(sequenceValue)}
        title={sequenceWithValues.label}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-8">
            {/*
              min-w-0 only, no overflow: SequenceEditorTable already owns the
              horizontal scroll container. A second `overflow-x-auto` here never
              scrolls - the inner container fills this one - but it does compute
              overflow-y to auto, which would clip the ColumnOverlay validation
              message into a stray vertical scrollbar rather than letting it show.
              min-w-0 supplies the flex shrink that overflow would otherwise have
              implied, so the row still collapses to fit the Energy AI panel.
            */}
            <div className="min-w-0">
              <SequenceEditorTable
                activeStage={highlightedStage}
                onAddStageClicked={() => handleAddStageClicked(sequenceValue)}
                onRemoveStageClicked={() => handleRemoveStageClicked(sequenceValue)}
                overlayProperties={overlayProperties}
                sequence={sequenceValue}
              >
                {rowTitles.map((row: string, rowIndex: number): React.ReactNode => {
                  const isRowDisabled =
                    sequenceValue?.table?.[rowIndex]?.stageValues?.every(
                      (stageValue: number) => stageValue === 0
                    ) ?? true;

                  const lockedByLabel = lockedByLabelByCompressor.get(row);
                  const isLockedByOther = !!lockedByLabel;

                  return (
                    <TableRow key={`${row}_row_${rowIndex}`}>
                      <EquipmentCell
                        icon={deriveCompressorSequencerIcon()}
                        isLockedByOther={isLockedByOther}
                        isRowDisabled={isRowDisabled}
                        leadingAdornment={
                          cosmicActive === true && !isRowDisabled && !isLockedByOther ? (
                            <EnergyAIControlIndicator />
                          ) : null
                        }
                        lockedByLabel={lockedByLabel}
                        onAddRow={() => handleAddRowClicked(sequenceValue, rowIndex)}
                        onRemoveRow={() => handleRemoveRowClicked(sequenceValue, rowIndex)}
                        rowTitle={rowTitles[rowIndex]}
                      />
                      {columnTitles.map((column: string, columnIndex: number) => {
                        // Adjusted column index because the table in the sequencer always
                        // has zero at the first index (all devices off).
                        const adjustedColumnIndex = columnIndex + 1;

                        // Get the stage value for the current cell.
                        const stageValue = getStageValue(
                          sequenceValue,
                          rowIndex,
                          adjustedColumnIndex
                        );

                        // Get the styles for a Cell in a Compressor Sequencer, based on the stage.
                        const { className, style } = getCompressorSequencerCellStyles(stageValue, {
                          isEnergyAIActive: cosmicActive === true,
                        });

                        // Calculate if this cell is dirty by comparing with original value
                        const originalValue = getStageValue(
                          originalSequence,
                          rowIndex,
                          adjustedColumnIndex
                        );
                        const isDirty = originalValue !== stageValue;

                        // Highlight the column if it matches the active stage
                        const isActiveColumn = highlightedStage === adjustedColumnIndex;
                        const isEnergyAIFallbackColumn =
                          energyAIFallbackStage === adjustedColumnIndex;
                        const tooltipContent = getCompressorSequenceState(stageValue);
                        const isUnavailable = stageValue === 0;

                        const cellDisabledTooltipText = isLockedByOther
                          ? `Remove this equipment from one sequencer before editing stages`
                          : disabledTooltipText;

                        return (
                          <SequenceCell
                            className={className}
                            disabledTooltipText={cellDisabledTooltipText}
                            isColumnActive={isActiveColumn || isEnergyAIFallbackColumn}
                            isDisabled={isLockedByOther || disabledByPermissions}
                            isDirty={isDirty}
                            isUnavailable={isUnavailable}
                            key={`${column}_cell_${row}_row_${rowIndex}_column_${adjustedColumnIndex}`}
                            onClick={() => {
                              handleStageClicked(sequenceValue, rowIndex, adjustedColumnIndex);
                            }}
                            original={
                              originalValue !== undefined
                                ? getCompressorSequenceState(originalValue)
                                : undefined
                            }
                            style={style}
                            tooltipContent={tooltipContent}
                          >
                            {getCompressorSequencerCellContent(stageValue)}
                          </SequenceCell>
                        );
                      })}
                      <AddStageColumnCell />
                    </TableRow>
                  );
                })}
                <TableRow data-component="CompressorSequencerStatusRow">
                  <TableCell className="h-[inherit] whitespace-nowrap border-b-1 border-l-0 px-2 text-xs font-bold uppercase text-ckds-text-fg-tier3">
                    Stage status
                  </TableCell>
                  {columnTitles.map((column: string, columnIndex: number) => {
                    const adjustedColumnIndex = columnIndex + 1;
                    const isFallbackStage = energyAIFallbackStage === adjustedColumnIndex;
                    const isActiveStage = highlightedStage === adjustedColumnIndex;

                    return (
                      <TableCell
                        className="h-[inherit] border-b-1 border-l-1 p-0"
                        key={`${column}_status_${adjustedColumnIndex}`}
                      >
                        <div className="flex h-full items-center justify-center bg-ckds-neutral-900 text-xs font-bold text-ckds-text-fg-tier3">
                          {isFallbackStage ? (
                            <FallbackStageStatusPill />
                          ) : isActiveStage ? (
                            <span className="rounded-sm bg-ckds-brand-500 px-1.5 py-0.5 text-xs font-bold leading-none text-ckds-neutral-950">
                              ACTIVE
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                    );
                  })}
                  <AddStageColumnCell />
                </TableRow>
              </SequenceEditorTable>
            </div>
            <CompressorEnergyAI
              compressorOrder={rowTitles}
              cosmicActive={cosmicActive}
              lockedCompressorNames={lockedCompressorNames}
              unavailableCompressorNames={unavailableCompressorNames}
            />
          </div>
          <SequencerLegend items={compressorLegendItems} />
          <SequencerInstructions id="compressor">
            <CompressorSequencerInstructionsContent />
          </SequencerInstructions>
        </div>
      </SequencerSectionTemplate>
    </Fragment>
  );
};

export const CompressorSequencerEditor = () => {
  const sequenceWithValues = useAtomValue(sequencesWithValuesAtom).at(0);
  if (!sequenceWithValues) {
    return null;
  }
  return <CompressorSequenceEditor sequenceWithValues={sequenceWithValues} />;
};
